// Shared by component-cart.js (quantity +/-, Remove) and component-
// product-form.js (Add to cart) - both tear down and rebuild the same
// <shipping-notice> on every cart update, so both need the same "figure
// out how many reward tiers this change crossed, then animate accordingly"
// logic rather than two copies of it drifting apart.
if ( ! window.buildShippingBarWidths ) {

	// oldNotice/newNotice: the <shipping-notice> element from just before
	// and just after an innerHTML swap (either can be null - e.g. a
	// genuinely first add has no oldNotice, an empty cart after removing
	// the last item has no newNotice). targetWidth: the new bar's own
	// already-computed final width (component-shipping-notice.js's own
	// init() already set this by the time this runs).
	//
	// Returns the width sequence to play and what the bar should reset to
	// between legs. Crossing tiers forward (adding enough to clear one or
	// more thresholds) fills each to 100% before resetting to 0% for the
	// next - crossing backward (removing enough to drop below one or more
	// thresholds) empties each to 0% before resetting to 100%, the same
	// motion in reverse - so either direction reads as actually leaving one
	// tier and entering another, not one plain slide between two unrelated
	// percentages (which is all a single width->width transition can ever
	// show once the segment itself has changed - see component-shipping-
	// notice.js's own segment-relative width comment).
	window.buildShippingBarWidths = function(oldNotice, newNotice, targetWidth) {

		if ( ! oldNotice || ! newNotice ) return { widths: [targetWidth], resetWidth: '0%' };

		let crossedForward = 0;
		let crossedBackward = 0;
		try {
			const tiers = JSON.parse(newNotice.getAttribute('data-tiers'));
			const oldTotal = Number(oldNotice.getAttribute('data-cart-total'));
			const newTotal = Number(newNotice.getAttribute('data-cart-total'));
			crossedForward = tiers.filter(tier => oldTotal < tier.threshold && newTotal >= tier.threshold).length;
			crossedBackward = tiers.filter(tier => newTotal < tier.threshold && oldTotal >= tier.threshold).length;
		} catch (e) {}

		if ( crossedForward > 0 ) {
			return { widths: [...Array(crossedForward).fill('100%'), targetWidth], resetWidth: '0%' };
		}
		if ( crossedBackward > 0 ) {
			return { widths: [...Array(crossedBackward).fill('0%'), targetWidth], resetWidth: '100%' };
		}
		return { widths: [targetWidth], resetWidth: '0%' };

	};

}

// Plays `widths` on the shipping bar one at a time, resetting (untransitioned)
// to `resetWidth` between legs so each one actually completes/empties before
// the next starts, rather than blending into it - see buildShippingBarWidths
// just above for how the sequence and reset point are chosen. bar.style at
// call time is expected to already be at the pre-change value with
// transition disabled (both call sites rewind to that before calling this);
// each step re-enables the transition itself before setting its own target.
if ( ! window.playTierCompleteFlash ) {

	// Celebration for a leg that just completed a tier (bar hit 100% on
	// its way to a NEW threshold, not just settling into its resting
	// position - see the isTierComplete check in playShippingBarSteps
	// below). Looked up fresh each time via the bar's own ancestor rather
	// than passed in, since this plays identically for the drawer's
	// hidden bar and the cart page's separately-cloned visible bar
	// (search cart-updated in theme.js) - each bar finds its own sibling
	// confetti container this way.
	//
	// A small confetti burst from the bar's own position, rather than a
	// wash or badge over the block, so it never has to compete with (or
	// hide behind) the congrats/text - the pieces fly OVER the content
	// briefly, which reads as normal for a burst of confetti in a way it
	// wouldn't for a solid fill.
	window.playTierCompleteFlash = function(bar, callback) {

		const notice = bar.closest('shipping-notice');
		const confetti = notice ? notice.querySelector('[data-js-tier-confetti]') : null;
		const pieces = confetti ? confetti.querySelectorAll('.cart-notice__confetti-piece') : null;
		if ( ! confetti || ! pieces || ! pieces.length ) { callback(); return; }

		// Evenly spread across an upward-opening fan (0deg = straight up)
		// rather than a full circle, so no piece ever launches downward
		// past the bar into whatever sits below the block - each piece
		// gets a random nudge off its even slot and its own distance/spin/
		// stagger so the burst doesn't look mechanically uniform, and a
		// colour cycled from the site's own gold/green accent palette
		// (already used elsewhere in this notice) rather than arbitrary
		// party colours.
		const palette = ['#4EB85B', '#72CD7B', '#F0C77A', '#D9A441', '#ffffff'];
		const spread = 150;
		const count = pieces.length;
		pieces.forEach((piece, i) => {
			const angle = (-spread / 2) + (spread / (count - 1)) * i + (Math.random() * 12 - 6);
			const rad = angle * Math.PI / 180;
			const distance = 42 + Math.random() * 32;
			piece.style.setProperty('--tx', (Math.sin(rad) * distance).toFixed(1) + 'px');
			piece.style.setProperty('--ty', (-Math.cos(rad) * distance).toFixed(1) + 'px');
			piece.style.setProperty('--rot', Math.round(Math.random() * 360) + 'deg');
			piece.style.setProperty('--delay', (Math.random() * 0.08).toFixed(2) + 's');
			piece.style.background = palette[i % palette.length];
		});

		let advanced = false;
		const finish = () => {
			if ( advanced ) return;
			advanced = true;
			confetti.classList.remove('settling');
			callback();
		};

		confetti.classList.add('bursting');
		setTimeout(() => {
			confetti.classList.remove('bursting');
			confetti.classList.add('settling');
			// No transitionend hook here (unlike the bar's own width steps) -
			// each piece transitions on its own staggered delay, so "the
			// last one to finish" isn't a single reliable event to key off;
			// a flat timeout matching the settle transition (0.4s,
			// component-sidebar.css) plus its max stagger is simpler and
			// just as safe.
			setTimeout(finish, 450);
		}, 650);

	};

}

if ( ! window.swapShippingBarText ) {

	// Fades the congrats/text lines out, swaps in the real final content
	// (captured before the caller staged the OLD content back in over it -
	// search onFinalLeg below, and swapShippingBarText's call sites in
	// component-cart.js/component-product-form.js), then fades back in.
	// Called once, right as the bar embarks on its real final leg (the
	// onFinalLeg hook just below) - not on every intermediate tier-
	// crossing leg, so a multi-tier crossing keeps showing the OLD text
	// through the whole celebratory bar sequence and only updates once,
	// in sync with the bar actually arriving, rather than jumping to the
	// final message before the bar has visually caught up to it.
	window.swapShippingBarText = function(notice, congratsHtml, textHtml) {

		const congratsEl = notice.querySelector('[data-js-free-shipping-congrats]');
		const textEl = notice.querySelector('[data-js-free-shipping-text]');
		const targets = [congratsEl, textEl].filter(Boolean);
		if ( ! targets.length ) return;

		targets.forEach(el => {
			el.style.transition = 'opacity 0.15s ease';
			el.style.opacity = '0';
		});
		setTimeout(() => {
			if ( congratsEl && congratsHtml !== null ) congratsEl.innerHTML = congratsHtml;
			if ( textEl && textHtml !== null ) textEl.innerHTML = textHtml;
			targets.forEach(el => void el.offsetWidth);
			targets.forEach(el => { el.style.opacity = '1'; });
		}, 150);

	};

}

if ( ! window.playShippingBarSteps ) {

	// onFinalLeg (optional): called once, right before the bar is set to
	// widths[i] on the leg where i is the LAST index - i.e. either the
	// only leg there is (a same-segment update, no tier crossed, nothing
	// to stage) or the settle-into-segment leg right after the last
	// reset in a multi-leg crossing. Callers use this to swap the
	// congrats/text content in sync with the bar, instead of it already
	// reading as the end state from the moment the section refreshes.
	window.playShippingBarSteps = function(bar, widths, resetWidth, i, onFinalLeg) {

		i = i || 0;

		if ( i === widths.length - 1 && onFinalLeg ) {
			onFinalLeg();
		}

		bar.style.transition = '';
		void bar.offsetWidth;
		bar.style.width = widths[i];

		if ( i >= widths.length - 1 ) return;

		// A leg that lands on 100% on its way to a forward (0%-anchored)
		// reset is an actual tier just being crossed, not the final resting
		// fill (that's i === widths.length - 1, already returned above) and
		// not a backward/decreasing change (which resets toward 100%, not
		// 0%) - only this case gets the celebration flash.
		const isTierComplete = widths[i] === '100%' && resetWidth === '0%';

		let advanced = false;
		const resetAndAdvance = () => {
			bar.style.transition = 'none';
			bar.style.width = resetWidth;
			void bar.offsetWidth;
			window.playShippingBarSteps(bar, widths, resetWidth, i + 1, onFinalLeg);
		};
		const advance = () => {
			if ( advanced ) return;
			advanced = true;
			bar.removeEventListener('transitionend', onTransitionEnd);
			if ( isTierComplete ) {
				window.playTierCompleteFlash(bar, resetAndAdvance);
			} else {
				resetAndAdvance();
			}
		};

		const onTransitionEnd = (event) => {
			if ( event.target !== bar || event.propertyName !== 'width' ) return;
			advance();
		};
		bar.addEventListener('transitionend', onTransitionEnd);

		// Safety net matching the bar's own transition (0.5s, component-
		// sidebar.css) in case transitionend never fires (prefers-reduced-
		// motion, or this leg's start/end width happened to be equal so
		// nothing actually transitioned).
		setTimeout(advance, 600);

	};

}

if ( typeof CartForm !== 'function' ) {
	class CartForm extends HTMLElement {

		constructor(){
			super();
			this.ajaxifyCartItems();
		}

		ajaxifyCartItems(){

			this.form = this.querySelector('form');

			this.querySelectorAll('[data-js-cart-item]').forEach(item=>{

				const remove = item.querySelector('.remove');
				if ( remove ) {
					remove.dataset.href = remove.getAttribute('href');
					remove.setAttribute('href', '');
					remove.addEventListener('click', (e)=>{
						e.preventDefault();
						this.updateCartQty(item, 0);
					})
				}

				const qty = item.querySelector('.qty');
				if ( qty ) {
					qty.addEventListener('input', debounce(e=>{
						e.preventDefault();
						e.target.blur();
						this.updateCartQty(item, parseInt(qty.value));
					}, 500));
					qty.addEventListener('click', (e)=>{
						e.target.select();
					})
				}

			})

		}

		updateCartQty(item, newQty){

			let alert = null;

			this.form.classList.add('processing');
			if ( this.querySelector('.alert') ) {
				this.querySelector('.alert').remove();
			}

			const body = JSON.stringify({
				line: parseInt(item.dataset.line),
				quantity: newQty
			});

			fetch(KROWN.settings.routes.cart_change_url, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json', 'Accept': 'application/javascript' },
					body
				})
				.then(response => response.json())
				.then(response => {
					if ( response.status == 422 ) {
						// wrong stock logic alert
						alert = document.createElement('span');
						alert.classList.add('alert', 'alert--error');
						if ( typeof response.description === 'string' ) {
							alert.innerHTML = response.description;
						} else {
							alert.innerHTML = response.message;
						}
					}
					return fetch('?section_id=helper-cart');
				})
				.then(response => response.text())
				.then(text => {

					const sectionInnerHTML = new DOMParser().parseFromString(text, 'text/html');
					const cartFormInnerHTML = sectionInnerHTML.getElementById('AjaxCartForm').innerHTML;
					const cartSubtotalInnerHTML = sectionInnerHTML.getElementById('AjaxCartSubtotal').innerHTML;

					const cartItems = document.getElementById('AjaxCartForm');

					// Same reasoning as component-product-form.js's onSubmitHandler
					// (search buildShippingBarWidths): the <shipping-notice> below
					// is about to be torn down and rebuilt already sitting at its
					// new, correct width, so there's nothing left to transition FROM
					// once that's happened - the old bar's width (and cart total, to
					// work out how many tiers this crosses, in either direction) has
					// to be grabbed first. Quantity +/- and Remove only ever happen
					// with the cart already open, so unlike the add-to-cart flow
					// there's no drawer-opening transition to hold for - a rAF to
					// let the rewind actually paint before playing the real sequence
					// is enough.
					const oldNotice = cartItems.querySelector('shipping-notice');
					const oldShippingBar = oldNotice ? oldNotice.querySelector('[data-js-free-shipping-slider]') : null;
					const oldShippingWidth = oldShippingBar ? oldShippingBar.style.width : null;
					const oldCongratsHtml = oldNotice ? oldNotice.querySelector('[data-js-free-shipping-congrats]').innerHTML : null;
					const oldTextHtml = oldNotice ? oldNotice.querySelector('[data-js-free-shipping-text]').innerHTML : null;

					cartItems.innerHTML = cartFormInnerHTML;
					cartItems.ajaxifyCartItems();

					// Handed off in the cart-updated CustomEvent's detail below so
					// theme.js's own listener can play the exact same sequence on
					// the cart PAGE's separate, visible shipping-notice (sections/
					// main-cart.liquid's own copy, outside #AjaxCartForm - search
					// cart-updated in theme.js). That one gets a plain innerHTML
					// clone of this hidden notice rather than living JS state, so
					// left to its own devices it would just paste in whatever
					// number happens to be current at that instant with nothing
					// animated - reusing the same widths/resetWidth is what lets it
					// play the identical fill instead.
					let shippingBarWidths = null;
					let shippingBarResetWidth = null;

					const newNotice = cartItems.querySelector('shipping-notice');
					const newShippingBar = newNotice ? newNotice.querySelector('[data-js-free-shipping-slider]') : null;
					if ( newShippingBar && oldShippingWidth !== null ) {
						const targetShippingWidth = newShippingBar.style.width;
						if ( targetShippingWidth !== oldShippingWidth ) {
							newShippingBar.style.transition = 'none';
							newShippingBar.style.width = oldShippingWidth;
							void newShippingBar.offsetWidth;

							// The new notice already carries its final, updated
							// congrats/text - stage the pre-update text back in so it
							// stays visible through the bar's fill/reset legs, and only
							// swap to the real content once onFinalLeg fires (see
							// swapShippingBarText above).
							const finalCongratsHtml = newNotice.querySelector('[data-js-free-shipping-congrats]').innerHTML;
							const finalTextHtml = newNotice.querySelector('[data-js-free-shipping-text]').innerHTML;
							if ( oldCongratsHtml !== null ) newNotice.querySelector('[data-js-free-shipping-congrats]').innerHTML = oldCongratsHtml;
							if ( oldTextHtml !== null ) newNotice.querySelector('[data-js-free-shipping-text]').innerHTML = oldTextHtml;

							const built = window.buildShippingBarWidths(oldNotice, newNotice, targetShippingWidth);
							shippingBarWidths = built.widths;
							shippingBarResetWidth = built.resetWidth;
							requestAnimationFrame(() => {
								window.playShippingBarSteps(newShippingBar, shippingBarWidths, shippingBarResetWidth, 0, () => {
									window.swapShippingBarText(newNotice, finalCongratsHtml, finalTextHtml);
								});
							});
						}
					}

					document.querySelectorAll('[data-header-cart-count]').forEach(elm=>{
						elm.textContent = cartItems.querySelector('[data-cart-count]').textContent;
					});
					document.querySelectorAll('[data-header-cart-total').forEach(elm=>{
						elm.textContent = cartItems.querySelector('[data-cart-total]').textContent;
					});

					if ( alert !== null ) {
						this.form.prepend(alert);
					} 

					document.getElementById('AjaxCartSubtotal').innerHTML = cartSubtotalInnerHTML;
							
					const event = new CustomEvent('cart-updated', { detail: { shippingBarWidths, shippingBarResetWidth } });
					this.dispatchEvent(event);

				})
				.catch(e => {
					console.log(e);
					let alert = document.createElement('span');
					alert.classList.add('alert', 'alert--error');
					alert.textContent = KROWN.settings.locales.cart_general_error;
					this.form.prepend(alert);
				})
				.finally(() => {
					this.form.classList.remove('processing');
				});
		}

	} 


  if ( typeof customElements.get('cart-form') == 'undefined' ) {
		customElements.define('cart-form', CartForm);
	}

}

if ( typeof CartProductQuantity !== 'function' ) {

	class CartProductQuantity extends HTMLElement {
		constructor(){
			super();
			this.querySelector('.qty-minus').addEventListener('click', this.changeCartInput.bind(this));
			this.querySelector('.qty-plus').addEventListener('click', this.changeCartInput.bind(this));
		}
		changeCartInput(){
			setTimeout(()=>{
				document.getElementById('AjaxCartForm').updateCartQty(this.closest('[data-js-cart-item]'), parseInt(this.querySelector('.qty').value));
			}, 50);
		}
	}

  if ( typeof customElements.get('cart-product-quantity') == 'undefined' ) {
		customElements.define('cart-product-quantity', CartProductQuantity);
	}

}

// method for apps to tap into and refresh the cart

if ( ! window.refreshCart ) {

	window.refreshCart = () => {
		
		fetch('?section_id=helper-cart')
			.then(response => response.text())
			.then(text => {

			const sectionInnerHTML = new DOMParser().parseFromString(text, 'text/html');
			const cartFormInnerHTML = sectionInnerHTML.getElementById('AjaxCartForm').innerHTML;
			const cartSubtotalInnerHTML = sectionInnerHTML.getElementById('AjaxCartSubtotal').innerHTML;

			const cartItems = document.getElementById('AjaxCartForm');
			cartItems.innerHTML = cartFormInnerHTML;
			cartItems.ajaxifyCartItems();

			document.querySelectorAll('[data-header-cart-count]').forEach(elm=>{
				elm.textContent = cartItems.querySelector('[data-cart-count]').textContent;
			});
			document.querySelectorAll('[data-header-cart-total').forEach(elm=>{
				elm.textContent = cartItems.querySelector('[data-cart-total]').textContent;
			})
			
			document.getElementById('AjaxCartSubtotal').innerHTML = cartSubtotalInnerHTML;
			if ( document.querySelector('[data-js-site-cart-sidebar]') ) {
				document.querySelector('[data-js-site-cart-sidebar]').show();
			}

			if ( document.querySelector('cart-recommendations') ) {
				document.querySelector('cart-recommendations').innerHTML = '';
				document.querySelector('cart-recommendations').generateRecommendations();
			}

		})

	}

}